<template>
  <div class="landing-shell" :data-layout="layoutMode">
    <Overlay :overlay="overlay" />
    <Snackbar :SnackBarComponent="SnackBarComponent" v-if="renderComp" />

    <Transition name="sidebar-scrim-fade">
      <div
        v-if="isCompactShell && drawer"
        class="sidebar-scrim"
        @click="drawer = false"
      />
    </Transition>
    <aside
      ref="sidebarAside"
      class="sidebar-float"
      :class="{
        'sidebar-float--open': sidebarIsVisible,
        'sidebar-float--collapsed': sidebarShowsRail,
        'sidebar-float--drawer': isCompactShell && drawer,
        'sidebar-float--animating': sidebarAnimating,
      }"
      @transitionend="onSidebarTransitionEnd"
    >
      <button
        v-if="isDesktop"
        type="button"
        class="sidebar-collapse-btn"
        :aria-label="sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'"
        :title="sidebarCollapsed ? 'Expand menu' : 'Collapse menu'"
        @click="toggleSidebarCollapse"
      >
        <v-icon icon="mdi-chevron-left" size="18" class="sidebar-collapse-btn__icon" />
      </button>

      <div class="sidebar-float__body">
      <div class="sidebar-brand" :class="{ 'sidebar-brand--rail': isSidebarRail }">
        <div class="sidebar-brand-mark">
          <v-img
            src="@/assets/logo2.jpg"
            alt="ServiceWRK"
            width="44"
            height="44"
            cover
            class="sidebar-logo-img"
          />
        </div>
        <div class="sidebar-brand-text sidebar-fade-text">
          <div class="sidebar-brand-title">ServiceWRK</div>
          <div class="sidebar-brand-tagline">Ticket Management</div>
        </div>
      </div>

      <div class="sidebar-section-label sidebar-fade-text">WORKSPACE</div>

      <div class="sidebar-nav-stack">
        <!-- Icon rail (always mounted — crossfades in) -->
        <div
          class="sidebar-nav-layer sidebar-nav-layer--rail"
          :class="{ 'is-active': isSidebarRail }"
        >
      <nav
        class="sidebar-rail"
        aria-label="Main navigation"
      >
        <template v-for="(item, idx) in RouterList" :key="`rail-${idx}`">
          <v-menu
            v-if="item.has_child_routes"
            location="end"
            :close-on-content-click="true"
            offset="10"
          >
            <template #activator="{ props: menuProps }">
              <v-tooltip :text="item.MenuName" location="end">
                <template #activator="{ props: tipProps }">
                  <button
                    type="button"
                    class="sidebar-rail-btn"
                    :class="getRailParentClass(item)"
                    :aria-label="item.MenuName"
                    v-bind="{ ...menuProps, ...tipProps }"
                  >
                    <AppIcon :name="item.MenuICon" :size="20" />
                  </button>
                </template>
              </v-tooltip>
            </template>
            <v-list density="compact" nav class="sidebar-flyout py-2">
              <v-list-subheader class="sidebar-flyout__title">{{ item.MenuName }}</v-list-subheader>
              <template v-for="(sitem, i) in item.SubMenu" :key="`rail-sub-${i}`"> 
                <v-list-item
                  v-if="!sitem.SubMenu || sitem.SubMenu.length === 0"
                  :to="sitem.MenuRoute"
                  class="nav-item nav-item--flyout"
                  :class="getChildActiveClass(sitem)"
                  @click="toggleSubMenu(item.MenuName, sitem.MenuName)"
                >
                  <template #prepend>
                    <AppIcon :name="sitem.MenuICon" :size="16" class="menu-icon" />
                  </template>
                  <v-list-item-title class="menu-font submenu-text">{{ sitem.MenuName }}</v-list-item-title>
                </v-list-item>
                <template v-else>
                  <v-list-subheader class="sidebar-flyout__subtitle">{{ sitem.MenuName }}</v-list-subheader>
                  <v-list-item
                    v-for="(ssitem, j) in sitem.SubMenu"
                    :key="`rail-nested-${j}`"
                    :to="ssitem.MenuRoute"
                    class="nav-item nav-item--flyout"
                    :class="getNestedChildActiveClass(ssitem)"
                    @click="master_setting_type = ssitem.MenuName"
                  >
                    <template #prepend>
                      <AppIcon :name="ssitem.MenuICon" :size="15" class="menu-icon" />
                    </template>
                    <v-list-item-title class="menu-font nested-submenu-text">{{ ssitem.MenuName }}</v-list-item-title>
                  </v-list-item>
                </template>
              </template>
            </v-list>
          </v-menu>

          <v-tooltip v-else :text="item.MenuName" location="end">
            <template #activator="{ props: tipProps }">
              <router-link
                v-bind="tipProps"
                :to="item.MenuRoute"
                class="sidebar-rail-btn"
                :class="getActiveClass(item)"
                :aria-label="item.MenuName"
                @click="checkRouteItem(item)"
              >
                <AppIcon :name="item.MenuICon" :size="20" />
              </router-link>
            </template>
          </v-tooltip>
        </template>
      </nav>
        </div>

        <!-- Full navigation (always mounted — crossfades out) -->
        <div
          class="sidebar-nav-layer sidebar-nav-layer--full"
          :class="{ 'is-active': !isSidebarRail }"
        >
      <v-list
        v-model:opened="openedMenus"
        density="compact"
        nav
        class="sidebar-nav"
      >
        <template v-for="(item, idx) in RouterList" :key="idx">
          <!-- Items without submenus -->
          <v-list-item
            v-if="!item.has_child_routes"
            :to="item.MenuRoute"
            class="nav-item"
            :class="getActiveClass(item)"
            @click="checkRouteItem(item)"
          >
            <div class="d-flex align-center">
              <AppIcon :name="item.MenuICon" :size="17" class="mr-3 menu-icon" />
              <span class="menu-font">{{ item.MenuName }}</span>
            </div>
          </v-list-item>

          <!-- Items with submenus -->
          <v-list-group v-else :value="item.MenuName" color="primary">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                class="nav-item nav-item--parent"
                @click="checkRouteItem(item)"
                :class="getParentActiveClass(item)"
              >
                <div class="d-flex align-center">
                  <AppIcon :name="item.MenuICon" :size="17" class="mr-3 menu-icon" />
                  <span class="menu-font">{{ item.MenuName }}</span>
                </div>
                <template v-slot:append>
                  <AppIcon name="mdi-chevron-down" :size="16" class="menu-chevron" />
                </template>
              </v-list-item>
            </template>

            <!-- Submenu items -->
            <template v-for="(sitem, i) in item.SubMenu" :key="i">
              <!-- Subitems without nested submenus -->
              <v-list-item
                v-if="!sitem.SubMenu || sitem.SubMenu.length === 0"
                :to="sitem.MenuRoute"
                class="nav-item nav-item--child"
                :class="getChildActiveClass(sitem)"
                @click="toggleSubMenu(item.MenuName, sitem.MenuName)"
              >
                <div class="d-flex align-center">
                  <AppIcon :name="sitem.MenuICon" :size="16" class="mr-3 menu-icon" />
                  <span class="menu-font submenu-text">{{
                    sitem.MenuName
                  }}</span>
                </div>
              </v-list-item>

              <!-- Subitems with nested submenus -->
              <v-list-group
                v-else
                :value="sitem.MenuName"
                color="primary"
                sub-group
              >
                <template v-slot:activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    class="nav-item nav-item--child nav-item--parent"
                    :class="getNestedParentActiveClass(sitem)"
                  >
                    <div class="d-flex align-center">
                      <AppIcon :name="sitem.MenuICon" :size="16" class="mr-3 menu-icon" />
                      <span class="menu-font submenu-text">{{
                        sitem.MenuName
                      }}</span>
                    </div>
                    <template v-slot:append>
                      <AppIcon name="mdi-chevron-down" :size="15" class="menu-chevron" />
                    </template>
                  </v-list-item>
                </template>

                <!-- Nested submenu items -->
                <v-list-item
                  v-for="(ssitem, j) in sitem.SubMenu"
                  :key="j"
                  :to="ssitem.MenuRoute"
                  class="nav-item nav-item--nested"
                  :class="getNestedChildActiveClass(ssitem)"
                  @click="master_setting_type = ssitem.MenuName"
                >
                  <div class="d-flex align-center">
                    <AppIcon :name="ssitem.MenuICon" :size="15" class="mr-3 menu-icon" />
                    <span class="menu-font nested-submenu-text">{{
                      ssitem.MenuName
                    }}</span>
                  </div>
                </v-list-item>
              </v-list-group>
            </template>
          </v-list-group>
        </template>
      </v-list>
        </div>
      </div>

      <div class="sidebar-footer" :class="{ 'sidebar-footer--rail': isSidebarRail }">
        <div v-if="isSidebarRail" class="sidebar-rail-footer">
          <v-tooltip :text="`${user_name} · ${userRoleLabel}`" location="end">
            <template #activator="{ props: tipProps }">
              <button
                type="button"
                class="sidebar-rail-footer__profile"
                :aria-label="`Signed in as ${user_name}`"
                v-bind="tipProps"
              >
                <span class="sidebar-rail-footer__avatar">{{ userInitials }}</span>
              </button>
            </template>
          </v-tooltip>
          <span class="sidebar-rail-footer__divider" aria-hidden="true" />
          <v-tooltip text="Sign out" location="end">
            <template #activator="{ props: tipProps }">
              <button
                type="button"
                class="sidebar-rail-footer__logout"
                aria-label="Sign out"
                v-bind="tipProps"
                @click="signOutMethod()"
              >
                <AppIcon name="mdi-logout" :size="18" />
              </button>
            </template>
          </v-tooltip>
        </div>

        <div v-else class="sidebar-user">
          <div class="sw-user-avatar">{{ userInitials }}</div>
          <div class="sidebar-user-info sidebar-fade-text">
            <div class="sidebar-user-name">{{ user_name }}</div>
            <div class="sidebar-user-role">{{ userRoleLabel }}</div>
          </div>
          <v-tooltip text="Sign out" location="top">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                icon
                variant="text"
                size="small"
                class="sidebar-logout"
                @click="signOutMethod()"
              >
                <AppIcon name="mdi-logout" :size="18" />
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>
      </div>
    </aside>

    <div class="main-column">
      <header class="app-bar-float">
        <div class="app-bar-float__start">
          <v-btn
            v-if="showMenuButton"
            icon
            variant="text"
            class="menu-btn"
            aria-label="Toggle navigation menu"
            :title="drawer ? 'Close menu' : 'Open menu'"
            @click.stop="drawer = !drawer"
          >
            <AppIcon name="mdi-menu" :size="22" />
          </v-btn>

          <div class="org-selector">
            <div class="org-selector-avatar">
              <v-img
                v-if="organizationLogoUrl"
                :src="organizationLogoUrl"
                width="36"
                height="36"
                cover
                class="org-selector-img"
              />
              <v-img
                v-else
                src="@/assets/logo2.jpg"
                width="36"
                height="36"
                cover
                class="org-selector-img"
              />
            </div>
            <div class="org-selector-text">
              <span class="org-selector-name">{{ organizationName }}</span>
              <span v-if="userEmail" class="org-selector-email">{{ userEmail }}</span>
              <span class="org-selector-role">{{ userRoleLabel }}</span>
            </div>
          </div>
        </div>

        <div class="app-bar-float__end">
          <v-btn
            v-if="isDealerUser"
            variant="outlined"
            size="small"
            class="dealer-btn text-none"
          >
            Dealer Portal
          </v-btn>

          <AppBarThemeControls
            :user-name="user_name"
            :user-email="userEmail"
            :user-role="userRoleLabel"
            :user-initials="userInitials"
            :report-disabled="overlay"
            :show-settings-menu="hasOrganizationSettingsAccess"
            @sign-out="signOutMethod"
            @report-issue="htmlToImageMethod"
          />
        </div>
      </header>

      <main class="app-content" ref="home">
        <router-view
          :master_setting_type="master_setting_type"
          :dataObject="dataObject"
          :dataReceived="dataReceived"
          @receivedData="dataReceived"
        />
      </main>
    </div>

    <OrganizationSettings
      v-if="showOrgSettingsHeadless"
      headless
      class="org-settings-headless-host"
    />
  </div>
</template>


<script>
import Overlay from "@/components/Extras/OverlayView.vue";
import Snackbar from "@/components/Extras/SnackbarView.vue";
import AppBarThemeControls from "@/components/Extras/AppBarThemeControls.vue";
import AppIcon from "@/components/Extras/AppIcon.vue";
import OrganizationSettings from "@/views/ChildHomePages/MastersSettings/OrganizationSettings.vue";
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { UpdateUserDetails } from "@/graphql/mutations.js";
import { GetCurrentUser } from "@/mixins/GetCurrentUser.js";
import { GetAllMyOrganizationsNew } from "@/mixins/GetAllMyOrganizations.js";
// import { fetchAndStoreOrgS3DataMethod } from "@/IndexedDB/IndexedDBSetter.js";
// import { getOrgS3DataMethod } from "@/IndexedDB/IndexedDBGetter.js";
import { signOut, getCurrentUser } from "aws-amplify/auth";
import { getS3UrlBasedData } from "@/mixins/Extras/GetS3UrlBasedData.js";
import { del } from "idb-keyval";
import { useAppShellLayout } from "@/composables/appShellLayout.js";
import { onOrgSettingsHostNeeded } from "@/utils/orgSettingsLauncher.js";
import { captureAppScreenshot } from "@/utils/captureHelpers.js";

export default {
  mixins: [GetCurrentUser, GetAllMyOrganizationsNew, getS3UrlBasedData],
  components: {
    Overlay,
    Snackbar,
    AppBarThemeControls,
    AppIcon,
    OrganizationSettings,
  },
  setup() {
    return { ...useAppShellLayout() };
  },
  data: () => ({
    subMenus: 0,
    subMenus2: 0,
    subMenuVmodel: 0,
    Connection: null,
    user_name: "",
    noDataText: "",
    Organizations: "",
    switch_organization: "",
    organization_logo_url: "",
    master_setting_type: "Service Types",
    drawer: false,
    sidebarCollapsed: true,
    sidebarAnimating: false,
    sidebarAnimTimer: null,
    CloseValue: false,
    renderComp: true,
    overlay: false,
    dataReceived: false,
    dialogCreateIssue: false,
    StoreObj: {},
    dataObject: {},
    SnackBarComponent: {},
    RouterList: [],
    RouterListEnterprise: [],
    selectedFilesFromDrop: [],
    openSubMenu: null,
    activeRoutes: {
      main: "",
      child: "",
      nested: "",
    },
    openedMenus: [],
    savedOpenedMenus: [],
    orgSettingsHostMounted: false,
  }),
  computed: {
    userInitials() {
      const name = this.user_name || "";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "SW";
    },
    userRoleLabel() {
      if (this.$store.getters.get_user_type === "AGENT") {
        return "Service Co-Ordinator";
      }
      return (this.$store.getters.get_user_type || "").replace(/_/g, " ");
    },
    organizationName() {
      return (
        this.$store.getters.get_current_user_details?.organization_details
          ?.organization_name || "ServiceWRK"
      );
    },
    organizationLogoUrl() {
      return (
        this.$store.getters.get_current_user_details?.organization_details
          ?.organization_logo_url || ""
      );
    },
    hasOrganizationSettingsAccess() {
      const findRoute = (items) => {
        if (!items?.length) return false;
        for (const item of items) {
          if (item.MenuRoute === "OrganizationSettings") return true;
          if (item.SubMenu?.length && findRoute(item.SubMenu)) return true;
        }
        return false;
      };
      return findRoute(this.RouterList);
    },
    showOrgSettingsHeadless() {
      return (
        this.orgSettingsHostMounted &&
        this.hasOrganizationSettingsAccess &&
        this.$route.name !== "OrganizationSettings"
      );
    },
    isDealerUser() {
      const type = this.$store.getters.get_current_user_details?.user_type;
      return type === "DEALER_OWNER" || type === "DEALER_AGENT";
    },
    userEmail() {
      return this.$store.getters.get_user_email || "";
    },
    isSidebarRail() {
      return (
        (this.isDesktop && this.sidebarCollapsed) ||
        (this.isTablet && !this.drawer)
      );
    },
    sidebarIsVisible() {
      if (this.isDesktop) return true;
      if (this.isTablet) return true;
      return this.drawer;
    },
    sidebarShowsRail() {
      if (this.isDesktop) return this.sidebarCollapsed;
      if (this.isTablet) return !this.drawer;
      return false;
    },
  },
  watch: {
    layoutMode(mode) {
      if (mode === "tablet") {
        this.sidebarCollapsed = true;
        this.openedMenus = [];
        this.drawer = false;
      } else if (mode === "mobile") {
        this.drawer = false;
        this.sidebarCollapsed = true;
        this.openedMenus = [];
      } else if (mode === "desktop") {
        const storedCollapsed = this.readSidebarCollapsedPreference();
        this.sidebarCollapsed = storedCollapsed;
        this.drawer = false;
        if (!storedCollapsed) {
          this.openedMenus = this.buildOpenedMenusFromActiveRoute();
        }
      }
    },
    "$route.name"() {
      this.overlay = false;
      this.updateActiveRoutes();
      if (this.isCompactShell) {
        this.drawer = false;
      }
    },
    "$store.getters.get_current_user_details"(val) {
      this.user_name = val.user_name;
      this.organization_name = val.organization_name;
    },
    switch_organization() {
      this.updateUserDetailsMethod();
      this.GetAllMyOrganization.filter((item) => {
        this.Organizations = item.organization_name;
      });
    },
  },
  async created() {
    await this.GetCurrentUser();
    if (
      this.$store.getters.get_current_user_details.organization_details
        .enable_credits_recharge == false
    ) {
      for (let i = 0; i < this.RouterList.length; i++) {
        if (this.RouterList[i].MenuRoute == "Transactions") {
          this.RouterList.splice(i, 1);
        }
      }
    }
    this.updateActiveRoutes();
  },
  async mounted() {
    this._unsubOrgSettingsHost = onOrgSettingsHostNeeded(() => {
      if (
        this.hasOrganizationSettingsAccess &&
        this.$route.name !== "OrganizationSettings" &&
        !this.orgSettingsHostMounted
      ) {
        this.orgSettingsHostMounted = true;
      }
    });

    try {
      const storedCollapsed = this.readSidebarCollapsedPreference();
      if (this.isDesktop) {
        this.sidebarCollapsed = storedCollapsed;
      } else if (this.isTablet) {
        this.sidebarCollapsed = true;
        this.drawer = false;
      } else {
        this.sidebarCollapsed = true;
        this.drawer = false;
      }
      if (this.sidebarCollapsed) {
        this.openedMenus = [];
      }
      await getCurrentUser();
      this.Router;
      this.updateNavigationValueMethod();
      this.GetAllMyOrganizationMethod();
      if (this.switch_organization == true) {
        this.GetAllMyOrganization.filter((item) => {
          this.Organizations = item.organization_name;
          this.updateUserDetailsMethod();
        });
      }
      // const orgDetails = this.$store.getters.get_current_user_details;
      await del("org_s3_data");
      // setTimeout(async () => {
      //   const indexedDbDataObj = await getOrgS3DataMethod();

      //   const currentOrgId = orgDetails.organization_id;
      //   const customerVersionId = orgDetails.organization_details.version_id;

      //   if (
      //     !indexedDbDataObj ||
      //     Object.keys(indexedDbDataObj).length === 0 ||
      //     indexedDbDataObj.current_org_id !== currentOrgId ||
      //     indexedDbDataObj.customer_data?.customer_version_id !==
      //       customerVersionId
      //   ) {
      //     await fetchAndStoreOrgS3DataMethod(orgDetails);
      //   } else {

      //   }
      // }, 2000);
      // Here to call Nanthu's New Code
      // await this.getS3UrlBasecDataMethod()
    } catch (error) {
      this.$router.push("/");
    }
  },
  beforeUnmount() {
    this._unsubOrgSettingsHost?.();
  },
  methods: {
    updateActiveRoutes() {
      const currentRoute = this.$route.name;
      this.activeRoutes.main = "";
      this.activeRoutes.child = "";
      this.activeRoutes.nested = "";
      for (const item of this.RouterList) {
        if (!item.has_child_routes && item.MenuRoute === currentRoute) {
          this.activeRoutes.main = item.MenuName;
          return;
        }
        if (item.has_child_routes && item.SubMenu) {
          for (const sitem of item.SubMenu) {
            if (!sitem.SubMenu && sitem.MenuRoute === currentRoute) {
              this.activeRoutes.main = item.MenuName;
              this.activeRoutes.child = sitem.MenuName;
              return;
            }
            if (sitem.SubMenu) {
              for (const ssitem of sitem.SubMenu) {
                if (ssitem.MenuRoute === currentRoute) {
                  this.activeRoutes.main = item.MenuName;
                  this.activeRoutes.child = sitem.MenuName;
                  this.activeRoutes.nested = ssitem.MenuName;
                  return;
                }
              }
            }
          }
        }
      }

      if (this.isSidebarRail) {
        return;
      }

      const opened = [];
      if (this.activeRoutes.main) opened.push(this.activeRoutes.main);
      if (this.activeRoutes.child) opened.push(this.activeRoutes.child);
      this.openedMenus = opened;
    },

    getActiveClass(item) {
      if (!item.has_child_routes && this.activeRoutes.main === item.MenuName) {
        return this.isSidebarRail ? "sidebar-rail-btn--active" : "nav-item--active";
      }
      return "";
    },

    getParentActiveClass(item) {
      if (
        item.has_child_routes &&
        this.activeRoutes.main === item.MenuName &&
        this.activeRoutes.child
      ) {
        return "nav-item--parent-open";
      }
      return "";
    },

    getRailParentClass(item) {
      if (this.activeRoutes.main === item.MenuName) {
        return "sidebar-rail-btn--active";
      }
      return "";
    },

    getChildActiveClass(sitem) {
      return this.activeRoutes.child === sitem.MenuName && !this.activeRoutes.nested
        ? "nav-item--active"
        : "";
    },

    getNestedParentActiveClass(sitem) {
      if (
        this.activeRoutes.child === sitem.MenuName &&
        this.activeRoutes.nested
      ) {
        return "nav-item--parent-open";
      }
      return "";
    },

    getNestedChildActiveClass(ssitem) {
      return this.activeRoutes.nested === ssitem.MenuName
        ? "nav-item--active"
        : "";
    },

    toggleSubMenu(parentMenuName, subMenuName) {
      this.openSubMenu = this.openSubMenu === subMenuName ? null : subMenuName;
    },

    toggleSidebarCollapse() {
      if (!this.isDesktop) {
        return;
      }

      if (this.sidebarAnimating) {
        return;
      }

      this.sidebarAnimating = true;

      if (!this.sidebarCollapsed) {
        this.savedOpenedMenus = [...this.openedMenus];
        this.sidebarCollapsed = true;
      } else {
        this.sidebarCollapsed = false;
        this.$nextTick(() => {
          this.openedMenus =
            this.savedOpenedMenus.length > 0
              ? [...this.savedOpenedMenus]
              : this.buildOpenedMenusFromActiveRoute();
        });
      }

      this.scheduleSidebarAnimFallback();
    },

    scheduleSidebarAnimFallback() {
      clearTimeout(this.sidebarAnimTimer);
      this.sidebarAnimTimer = setTimeout(() => {
        this.finishSidebarAnimation();
      }, 420);
    },

    onSidebarTransitionEnd(event) {
      if (event.propertyName !== "width") {
        return;
      }
      if (event.target !== this.$refs.sidebarAside) {
        return;
      }

      this.finishSidebarAnimation();
    },

    finishSidebarAnimation() {
      if (!this.sidebarAnimating) {
        return;
      }

      clearTimeout(this.sidebarAnimTimer);
      this.sidebarAnimating = false;

      if (this.sidebarCollapsed) {
        this.openedMenus = [];
      }

      localStorage.setItem(
        "sw-sidebar-collapsed",
        this.sidebarCollapsed ? "1" : "0",
      );
    },

    readSidebarCollapsedPreference() {
      const stored = localStorage.getItem("sw-sidebar-collapsed");
      // No stored preference → default collapsed; "0" = expanded; "1" = collapsed
      return stored !== "0";
    },

    buildOpenedMenusFromActiveRoute() {
      const opened = [];
      if (this.activeRoutes.main) opened.push(this.activeRoutes.main);
      if (this.activeRoutes.child) opened.push(this.activeRoutes.child);
      return opened;
    },

    async signOutMethod() {
      // await signOut({ global: true });
      await signOut();
      this.$router.push("/");
    },

    checkRouteItem(item) {
      if (this.isCompactShell) {
        this.drawer = false;
      }
      if (
        (this.$route.name != item.MenuRoute && item.has_child_routes == true) ||
        item.has_child_routes == false
      ) {
        if (item.MenuRoute === "Tickets") {
          const getCurrent = this.$store.getters.get_current_user_details;
          if (
            this.$store.getters.get_current_user_details.user_type ===
              "DEALER_OWNER" ||
            this.$store.getters.get_current_user_details.user_type ===
              "DEALER_AGENT"
          ) {
            if (getCurrent.enable_active_ticket !== false) {
              setTimeout(() => {
                this.$router.push("/ActiveTickets");
              }, 1000);
            } else if (getCurrent.enable_partially_closed_ticket !== false) {
              setTimeout(() => {
                this.$router.push("/PartiallyClosedTickets");
              }, 1000);
            } else if (getCurrent.enable_closed_ticket !== false) {
              setTimeout(() => {
                this.$router.push("/ClosedTickets");
              }, 1000);
            }
          } else {
            this.overlay = true;
            setTimeout(() => {
              this.$router.push("/ActiveTickets");
              this.overlay = false;
            }, 1000);
          }
        } else {
          this.$router.push(item.MenuRoute);
        }
        switch (item.MenuRoute) {
          case "ActiveTickets":
            this.master_setting_type = "Active Tickets";
            break;
        }
      }
    },

    Method() {
      this.captureIssueScreenshotAndOpenReport();
    },

    htmlToImageMethod() {
      this.captureIssueScreenshotAndOpenReport();
    },

    async captureIssueScreenshotAndOpenReport() {
      this.selectedFilesFromDrop = [];

      try {
        const blob = await captureAppScreenshot();
        this.selectedFilesFromDrop.push(blob);

        const issueDetails = {
          selectedFilesFromDrop: this.selectedFilesFromDrop,
          routeName: this.$route.name,
        };
        this.$store.commit("SET_ISSUE_DETAILS", issueDetails);
        this.$store.commit("SET_PREVIOUS_ROUTE", this.$route.name);

        this.overlay = true;
        await this.$router.push("ReportIssue");
      } catch (error) {
        this.renderComp = false;
        this.$nextTick(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText:
              error?.message || "Could not capture screenshot. Please try again.",
          };
          this.renderComp = true;
        });
      } finally {
        this.overlay = false;
      }
    },

    updateNavigationValueMethod() {
      let previous_route = this.$store.getters.get_previous_route;
      if (previous_route && previous_route != "") {
        switch (previous_route) {
          case "Dashboard":
            this.selectedItem = 0;
            break;
          case "Attendance":
            this.selectedItem = 1;
            break;
          case "ComponentTransactionHistory":
            this.selectedItem = 2;
            break;
          case "TransactionLogs":
            this.selectedItem = 3;
            break;
          case "Logs":
            this.selectedItem = 4;
            break;
          case "Tickets":
            this.selectedItem = 5;
            break;
          case "RFTs":
            this.selectedItem = 6;
            break;
          case "ScheduledMaintainance":
            this.selectedItem = 7;
            break;
          case "Customers":
            this.selectedItem = 8;
            break;
          case "ServiceCatalog":
            this.selectedItem = 9;
            break;
          case "Territory":
            this.selectedItem = 10;
            break;
          case "ListComponents":
            this.selectedItem = 11;
            break;
          case "ListInventory":
            this.selectedItem = 12;
            break;
          case "ServiceCenter":
            this.selectedItem = 13;
            break;
          case "quotation":
            this.selectedItem = 14;
            break;
          case "Templates":
            this.selectedItem = 15;
            break;
          case "Contract":
            this.selectedItem = 16;
            break;
          case "Contract Templates":
            this.selectedItem = 17;
            break;
          case "Transactions":
            this.selectedItem = 18;
            break;
          case "Users":
            this.selectedItem = 19;
            break;
          case "ServiceRepresentatives":
            this.selectedItem = 20;
            break;
          case "Partner":
            this.selectedItem = 21;
            break;
          case "Dealers":
            this.selectedItem = 22;
            break;
          case "MasterSetting":
            this.selectedItem = 23;
            break;
        }
      }
      this.$forceUpdate();
    },

    async switchOrganization() {
      await this.GetCurrentUser();
      if (
        this.$store.getters.get_current_user_details.organization_details
          .enable_credits_recharge == false
      ) {
        for (let i = 0; i < this.RouterList.length; i++) {
          if (this.RouterList[i].MenuRoute == "Transactions") {
            this.RouterList.splice(i, 1);
          }
        }
      }
    },

    async updateUserDetailsMethod() {
      try {
        this.overlay = true;
        let result = await client.graphql({
          query: UpdateUserDetails,
          variables: {
            input: {
              updater_user_id:
                this.$store.getters.get_current_user_details.user_id,
              organization_id:
                this.$store.getters.get_current_user_details.organization_id,
              is_switch_between_organization: "true",
              new_organization_id: this.switch_organization,
            },
          },
        });

        this.switchOrganization();
        var ResultObject = JSON.parse(result.data.UpdateUserDetails);
        if (ResultObject.status == "SUCCESS") {
          setTimeout(() => this.$router.push("/Dashboard"), 500);
          this.renderComp = false;
          this.$nextTick(() => {
            this.SnackBarComponent = {
              SnackbarVmodel: true,
              SnackbarColor: "green",
              Top: true,
              SnackbarText: ResultObject.status_message,
            };
            this.renderComp = true;
          });
        }
        this.overlay = false;
      } catch (error) {
        this.renderComp = false;
        this.$nextTick(() => {
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "red",
            Top: true,
            SnackbarText: error.errors[0].message,
          };
          this.renderComp = true;
        });
        this.overlay = false;
        this.noDataText = error.errors[0].message;
      }
    },

    dialogCreateIssueEmit() {
      this.dialogCreateIssue = false;
      this.selectedFilesFromDrop = [];
    },
  },
};
</script>

<style scoped>
.landing-shell {
  --shell-gap: 16px;
  --shell-pad: 16px;
  --panel-radius: 24px;
  --sidebar-ease: cubic-bezier(0.32, 0.72, 0, 1);
  --sidebar-duration: 0.4s;
  --sidebar-width-expanded: 272px;
  --sidebar-width-collapsed: 80px;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  gap: var(--shell-gap);
  padding: var(--shell-pad);
  box-sizing: border-box;
  background: var(--sw-gradient);
}

.landing-shell::before {
  content: "";
  position: fixed;
  inset: 0;
  background: var(--sw-gradient-radial);
  pointer-events: none;
  z-index: 0;
}

.sidebar-scrim {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.4);
  z-index: 40;
}

.sidebar-scrim-fade-enter-active,
.sidebar-scrim-fade-leave-active {
  transition: opacity var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-scrim-fade-enter-from,
.sidebar-scrim-fade-leave-to {
  opacity: 0;
}

.sidebar-float {
  position: relative;
  z-index: 2;
  width: var(--sidebar-width-expanded);
  flex: 0 0 var(--sidebar-width-expanded);
  flex-shrink: 0;
  align-self: stretch;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--sw-shell-glass-bg);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  border-radius: var(--panel-radius);
  border: 1px solid var(--sw-shell-glass-border);
  box-shadow: var(--sw-shell-glass-shadow), var(--sw-shell-glass-inset);
  overflow: visible;
  isolation: isolate;
  transform: translateZ(0);
  transition:
    width var(--sidebar-duration) var(--sidebar-ease),
    flex-basis var(--sidebar-duration) var(--sidebar-ease),
    box-shadow var(--sidebar-duration) var(--sidebar-ease),
    background var(--sidebar-duration) var(--sidebar-ease),
    border-color var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-float::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--sw-shell-glass-gradient);
  pointer-events: none;
  z-index: 0;
}

.sidebar-float::after {
  content: "";
  position: absolute;
  top: -40px;
  right: -30px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: var(--sw-shell-glass-glow);
  pointer-events: none;
  z-index: 0;
}

.sidebar-float--collapsed {
  width: var(--sidebar-width-collapsed);
  flex-basis: var(--sidebar-width-collapsed);
}

.sidebar-float--animating {
  pointer-events: none;
  will-change: width;
}

.sidebar-float--animating .sidebar-collapse-btn {
  pointer-events: auto;
}

.sidebar-float__body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-collapse-btn {
  position: absolute;
  right: -14px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--sw-border);
  border-radius: 999px;
  background: var(--sw-surface);
  color: var(--sw-muted);
  box-shadow: var(--sw-shadow-sm);
  cursor: pointer;
  transition:
    color 0.28s var(--sidebar-ease),
    border-color 0.28s var(--sidebar-ease),
    box-shadow 0.28s var(--sidebar-ease);
}

.sidebar-collapse-btn__icon {
  transition: transform var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-float--collapsed .sidebar-collapse-btn__icon {
  transform: rotate(180deg);
}

.sidebar-collapse-btn:hover {
  color: var(--sw-primary);
  border-color: rgba(var(--sw-primary-rgb), 0.28);
  box-shadow: 0 4px 14px rgba(var(--sw-primary-rgb), 0.14);
}

/* Smooth fade for labels / footer text */
.sidebar-fade-text {
  opacity: 1;
  max-width: 220px;
  overflow: hidden;
  white-space: nowrap;
  transition:
    opacity calc(var(--sidebar-duration) * 0.85) var(--sidebar-ease),
    max-width var(--sidebar-duration) var(--sidebar-ease),
    margin var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-float--collapsed .sidebar-fade-text {
  opacity: 0;
  max-width: 0;
  margin: 0 !important;
  padding: 0 !important;
  pointer-events: none;
}

.sidebar-footer {
  transition: padding var(--sidebar-duration) var(--sidebar-ease);
}

/* Crossfade nav layers */
.sidebar-nav-stack {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-nav-layer {
  position: absolute;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translate3d(-6px, 0, 0);
  transition:
    opacity calc(var(--sidebar-duration) * 0.7) var(--sidebar-ease),
    visibility calc(var(--sidebar-duration) * 0.7) var(--sidebar-ease),
    transform var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-nav-layer--rail {
  transform: translate3d(6px, 0, 0);
}

.sidebar-nav-layer.is-active {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translate3d(0, 0, 0);
}

.sidebar-nav-layer--full.is-active {
  transition-delay: 0.05s;
}

.sidebar-nav-layer--rail.is-active {
  transition-delay: 0.12s;
}

.sidebar-nav-layer:not(.is-active) {
  transition-delay: 0s;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 28px 22px 20px;
  flex-shrink: 0;
  transition:
    padding var(--sidebar-duration) var(--sidebar-ease),
    gap var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-user-info {
  flex: 1;
  min-width: 0;
}

.sidebar-user {
  transition: gap var(--sidebar-duration) var(--sidebar-ease);
}

.sidebar-brand--rail {
  justify-content: center;
  padding: 22px 10px 16px;
  gap: 0;
}

.sidebar-brand--rail .sidebar-brand-mark {
  width: 42px;
  height: 42px;
}

.sidebar-brand-mark {
  width: 48px;
  height: 48px;
  padding: 3px;
  border-radius: 14px;
  background: var(--sw-surface);
  box-shadow: 0 2px 12px rgba(var(--sw-primary-rgb), 0.12);
  overflow: hidden;
}

.sidebar-logo-img {
  border-radius: 11px;
}

.sidebar-brand-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--sw-text);
  line-height: 1.2;
}

.sidebar-brand-tagline {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--sw-muted);
}

.sidebar-section-label {
  padding: 8px 20px 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--sw-section-label);
}

.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 10px 12px !important;
  background: transparent !important;
  color: var(--sw-nav-text) !important;
}

/* Collapsed rail — icon buttons only */
.sidebar-rail {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 0 12px;
  width: 100%;
  background: transparent !important;
}

/* Nav drawer — scrollbar visible only on hover */
.sidebar-nav-layer,
.sidebar-nav,
.sidebar-rail {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sidebar-nav-layer:hover,
.sidebar-nav:hover,
.sidebar-rail:hover {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--sw-primary-rgb), 0.32) transparent;
}

.sidebar-nav-layer::-webkit-scrollbar,
.sidebar-nav::-webkit-scrollbar,
.sidebar-rail::-webkit-scrollbar {
  width: 5px;
}

.sidebar-nav-layer::-webkit-scrollbar-track,
.sidebar-nav::-webkit-scrollbar-track,
.sidebar-rail::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav-layer::-webkit-scrollbar-thumb,
.sidebar-nav::-webkit-scrollbar-thumb,
.sidebar-rail::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: transparent;
  transition: background 0.2s ease;
}

.sidebar-nav-layer:hover::-webkit-scrollbar-thumb,
.sidebar-nav:hover::-webkit-scrollbar-thumb,
.sidebar-rail:hover::-webkit-scrollbar-thumb {
  background: rgba(var(--sw-primary-rgb), 0.28);
}

html[data-theme="dark"] .sidebar-nav-layer:hover,
html[data-theme="dark"] .sidebar-nav:hover,
html[data-theme="dark"] .sidebar-rail:hover {
  scrollbar-color: rgba(var(--sw-primary-rgb), 0.45) transparent;
}

html[data-theme="dark"] .sidebar-nav-layer:hover::-webkit-scrollbar-thumb,
html[data-theme="dark"] .sidebar-nav:hover::-webkit-scrollbar-thumb,
html[data-theme="dark"] .sidebar-rail:hover::-webkit-scrollbar-thumb {
  background: rgba(var(--sw-primary-rgb), 0.42);
}

.sidebar-rail > * {
  display: flex;
  justify-content: center;
  width: 100%;
  flex-shrink: 0;
}

.sidebar-rail-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--sw-nav-icon);
  text-decoration: none;
  cursor: pointer;
}

.sidebar-rail-btn:hover {
  background: var(--sw-hover-bg);
  color: var(--sw-text);
}

.sidebar-rail-btn--active,
.sidebar-rail-btn.nav-item--active {
  background: rgba(var(--sw-primary-rgb), 0.1) !important;
  color: var(--sw-primary) !important;
}

.sidebar-rail-btn--active::before,
.sidebar-rail-btn.nav-item--active::before {
  content: "";
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 999px;
  background: var(--sw-primary);
}

.sidebar-rail-btn .app-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.sidebar-rail-btn .app-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.sidebar-flyout {
  min-width: 220px;
  max-width: 280px;
  border-radius: 16px !important;
  border: 1px solid var(--sw-border);
  box-shadow: var(--sw-shadow-panel) !important;
  background: var(--sw-surface) !important;
}

.sidebar-flyout__title {
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  color: var(--sw-primary) !important;
  text-transform: uppercase;
}

.sidebar-flyout__subtitle {
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: var(--sw-muted) !important;
  min-height: 28px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--flyout) {
  border-radius: 10px !important;
  margin: 2px 8px !important;
  min-height: 38px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--flyout.nav-item--active) {
  background: rgba(var(--sw-primary-rgb), 0.1) !important;
}

.menu-font {
  font-size: 0.84rem !important;
  font-weight: 600;
  color: var(--sw-nav-text);
  line-height: 1.3;
}

.submenu-text {
  font-size: 0.8rem !important;
  color: var(--sw-nav-text);
}

.nested-submenu-text {
  font-size: 0.76rem !important;
  color: var(--sw-text-secondary);
}

.menu-icon {
  color: var(--sw-nav-icon);
  margin-right: 10px;
  flex-shrink: 0;
}

.menu-chevron {
  color: var(--sw-nav-icon);
}

.sidebar-rail-btn:active .app-icon :deep(svg),
.sidebar-logout:active .app-icon :deep(svg),
.sidebar-nav :deep(.v-list-item.nav-item:active .menu-icon svg),
.sidebar-flyout :deep(.v-list-item.nav-item--flyout:active .menu-icon svg) {
  animation: sidebar-nav-icon-press 0.38s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;
}

@keyframes sidebar-nav-icon-press {
  0% {
    transform: scale(1) rotate(0deg);
  }

  50% {
    transform: scale(1.12) rotate(14deg);
  }

  100% {
    transform: scale(1) rotate(0deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-rail-btn:active .app-icon :deep(svg),
  .sidebar-logout:active .app-icon :deep(svg),
  .sidebar-nav :deep(.v-list-item.nav-item:active .menu-icon svg),
  .sidebar-flyout :deep(.v-list-item.nav-item--flyout:active .menu-icon svg) {
    animation: sidebar-nav-icon-tap 0.18s ease;
  }

  @keyframes sidebar-nav-icon-tap {
    0% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.08);
    }

    100% {
      transform: scale(1);
    }
  }
}

/* Base nav item — pill shape */
.sidebar-nav :deep(.v-list-item.nav-item) {
  position: relative;
  border-radius: 999px !important;
  margin: 3px 4px !important;
  min-height: 42px !important;
  padding-inline: 14px 12px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--child) {
  margin-left: 6px !important;
  margin-right: 4px !important;
  min-height: 38px !important;
  width: calc(100% - 6px);
  padding-inline-start: 12px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--nested) {
  margin-left: 8px !important;
  margin-right: 4px !important;
  min-height: 36px !important;
  width: calc(100% - 8px);
  padding-inline-start: 10px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item:hover) {
  background: var(--sw-hover-bg) !important;
}

.sidebar-nav :deep(.v-list-item--active) {
  background: transparent !important;
  color: inherit !important;
}

/* Active route — tonal pill + left indicator bar */
.sidebar-nav :deep(.v-list-item.nav-item--active) {
  background: rgba(var(--sw-primary-rgb), 0.1) !important;
  padding-left: 20px !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--active::before) {
  content: "";
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 999px;
  background: var(--sw-primary);
}

.sidebar-nav :deep(.v-list-item.nav-item--child.nav-item--active::before) {
  left: 5px;
}

.sidebar-nav :deep(.v-list-item.nav-item--nested.nav-item--active::before) {
  left: 3px;
}

.sidebar-nav :deep(.v-list-item.nav-item--active .menu-font) {
  color: var(--sw-primary) !important;
  font-weight: 600 !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--active .menu-icon) {
  color: var(--sw-primary) !important;
}

/* Parent group open — purple label, no pill fill */
.sidebar-nav :deep(.v-list-item.nav-item--parent-open) {
  background: transparent !important;
}

.sidebar-nav :deep(.v-list-item.nav-item--parent-open .menu-font),
.sidebar-nav :deep(.v-list-item.nav-item--parent-open .menu-icon),
.sidebar-nav :deep(.v-list-item.nav-item--parent-open .menu-chevron) {
  color: var(--sw-primary) !important;
  font-weight: 600 !important;
}

/* List groups — submenu container (indented + guide line) */
.sidebar-nav :deep(.v-list-group__items) {
  background: transparent !important;
  --indent-padding: 0;
  margin-left: 16px;
  margin-right: 6px;
  padding: 2px 0 4px 12px;
  border-left: 2px solid rgba(var(--sw-primary-rgb), 0.14);
}

.sidebar-nav :deep(.v-list-group__items .v-list-group__items) {
  margin-left: 10px;
  padding-left: 10px;
  border-left-color: rgba(var(--sw-primary-rgb), 0.1);
}

.sidebar-nav :deep(.v-list-group--open > .v-list-group__header .menu-chevron) {
  transform: rotate(180deg);
  color: var(--sw-primary) !important;
}

.sidebar-nav :deep(.v-list-group--open > .v-list-group__header.nav-item--parent-open) {
  background: rgba(var(--sw-primary-rgb), 0.05) !important;
}

.sidebar-nav :deep(.v-list-group__header) {
  padding-inline-end: 8px !important;
}

.sidebar-nav :deep(.v-list-item__append) {
  margin-inline-start: 4px !important;
}

html[data-theme="dark"] .sidebar-nav :deep(.v-list-item.nav-item--active) {
  background: rgba(var(--sw-primary-rgb), 0.18) !important;
}

html[data-theme="dark"] .sidebar-nav :deep(.v-list-group--open > .v-list-group__header.nav-item--parent-open) {
  background: rgba(var(--sw-primary-rgb), 0.08) !important;
}

.sidebar-footer {
  margin-top: auto;
  padding: 16px 18px 22px;
  border-top: 1px solid var(--sw-footer-border);
  flex-shrink: 0;
}

.sidebar-footer--rail {
  padding: 10px 0 14px;
  display: flex;
  justify-content: center;
}

.sidebar-rail-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 52px;
  padding: 8px 6px;
  border-radius: 16px;
  background: var(--sw-shell-glass-bg, rgba(255, 255, 255, 0.72));
  border: 1px solid var(--sw-shell-glass-border, rgba(236, 228, 240, 0.95));
  box-shadow:
    0 4px 14px rgba(var(--sw-primary-rgb), 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.sidebar-rail-footer__profile,
.sidebar-rail-footer__logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 12px;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
}

.sidebar-rail-footer__profile {
  width: 40px;
  height: 40px;
}

.sidebar-rail-footer__profile:hover {
  background: rgba(var(--sw-primary-rgb), 0.08);
}

.sidebar-rail-footer__avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(
    --sw-avatar-gradient,
    linear-gradient(135deg, #7b046c 0%, #a21caf 52%, #c026d3 100%)
  );
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(var(--sw-primary-rgb), 0.28);
}

.sidebar-rail-footer__divider {
  display: block;
  width: 24px;
  height: 1px;
  background: var(--sw-footer-border, rgba(236, 228, 240, 0.95));
  flex-shrink: 0;
}

.sidebar-rail-footer__logout {
  width: 40px;
  height: 36px;
  color: var(--sw-muted);
}

.sidebar-rail-footer__logout:hover {
  background: rgba(220, 38, 38, 0.08);
  color: #dc2626;
}

.sidebar-rail-footer__logout:active,
.sidebar-rail-footer__profile:active {
  transform: scale(0.96);
}

.sidebar-rail-footer__logout:active .app-icon :deep(svg) {
  animation: sidebar-nav-icon-press 0.38s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sidebar-avatar,
.sw-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(
    --sw-avatar-gradient,
    linear-gradient(90deg, #7b046c 0%, #a21caf 42%, #c026d3 72%, #d61de2 100%)
  );
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  box-shadow: 0 2px 10px rgba(var(--sw-primary-rgb), 0.35);
}

.sidebar-user-info {
  flex: 1;
  min-width: 0;
}

.sidebar-user-name {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--sw-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-role {
  font-size: 0.72rem;
  color: var(--sw-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-logout {
  color: var(--sw-muted) !important;
}

.main-column {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--shell-gap);
  overflow: hidden;
  contain: layout style;
}

.app-bar-float {
  position: relative;
  z-index: 1;
  isolation: isolate;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 64px;
  padding: 12px 20px;
  overflow: hidden;
  background: var(--sw-shell-glass-bg);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  border-radius: var(--panel-radius);
  border: 1px solid var(--sw-shell-glass-border);
  box-shadow: var(--sw-shell-glass-inset);
  transition:
    background 0.3s ease,
    border-color 0.3s ease;
}

.app-bar-float::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--sw-shell-glass-gradient);
  pointer-events: none;
  z-index: 0;
}

.app-bar-float__start {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1 1 auto;
}

.app-bar-float__end {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
  flex: 0 0 auto;
}

.app-bar-float__start > .menu-btn,
.app-bar-float__end > .dealer-btn,
.app-bar-float__end > .app-bar-controls {
  flex-shrink: 0;
}

.menu-btn {
  color: var(--sw-app-bar-icon) !important;
}

.org-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 8px 4px 4px;
  border-radius: 14px;
}

.org-selector-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(var(--sw-primary-rgb), 0.12);
}

.org-selector-img {
  border-radius: 12px;
}

.org-selector-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.org-selector-name {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--sw-text);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-selector-email {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--sw-text-secondary);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-selector-role {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--sw-muted);
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dealer-btn {
  border-radius: 999px !important;
  border-color: var(--sw-dealer-border) !important;
  color: var(--sw-primary) !important;
  font-weight: 600;
  background: var(--sw-surface) !important;
}

.app-content {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-content :deep(.tickets-page-host) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-content :deep(.tag-customers-root),
.app-content :deep(.dealers-tag-customers-host),
.app-content :deep(.service-partners-tag-customers-host),
.app-content :deep(.territories-view),
.app-content :deep(.territories-tag-customers-host),
.app-content :deep(.territories-tag-service-center-host),
.app-content :deep(.territories-pincodes-host),
.app-content :deep(.dealers-products-host),
.app-content :deep(.customers-page-host),
.app-content :deep(.customer-details-shell),
.app-content :deep(.customer-create-edit-shell),
.app-content :deep(.customer-locations-root),
.app-content :deep(.customer-locations-shell),
.app-content :deep(.vendors-page-host),
.app-content :deep(.vendors-create-card-root),
.app-content :deep(.vendors-mapping-card-root),
.app-content :deep(.vendors-create-edit-shell),
.app-content :deep(.vendors-mapping-shell) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-content :deep(.create-ticket-host) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-content :deep(.dashboard-container),
.app-content :deep(.dashboard-page) {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  background: transparent;
}

/* Default card — full white panel (dashboard widgets, dialogs, etc.) */
.app-content :deep(.v-card) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--sw-surface);
  border-radius: var(--panel-radius);
  border: 1px solid var(--sw-border);
  box-shadow: var(--sw-shadow-sm);
  transition: background 0.3s ease, border-color 0.3s ease;
}

/* Customer Details tab — content must size to children so parent host can scroll */
.app-content :deep(.customer-details-view-card),
.app-content :deep(.customer-details-tabs--details-host .ticket-details-page-card),
.app-content :deep(.customer-details-view-root > .v-card) {
  flex: 0 0 auto !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  display: block !important;
}

.app-content :deep(.customer-details-shell.ticket-details-shell),
.app-content :deep(.ticket-details-shell.customer-details-shell) {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.app-content :deep(.customer-details-shell > .ticket-details__content) {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  max-height: 100% !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.app-content :deep(.customer-details-tabs--details-host) {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  max-height: 100% !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* List pages: shell is transparent; header on gradient (see page-content-header.css) */
.app-content :deep(.v-card:has(> .v-toolbar)),
.app-content :deep(.v-card:has(> .page-content-header)),
.app-content :deep(.v-card:has(> .v-row .v-toolbar)) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  gap: 10px;
  padding: 0 !important;
}

.app-content :deep(.v-card > .v-toolbar),
.app-content :deep(.v-card > .page-content-header) {
  flex-shrink: 0;
  background: transparent !important;
  color: var(--sw-text) !important;
}

/* Table body card when header is outside (see sw-table-layout.css + sw-data-table.css) */
.app-content :deep(.v-card:has(> .v-toolbar) .v-card-text:has(> .v-data-table:only-child)),
.app-content :deep(.v-card:has(> .page-content-header) .v-card-text:has(> .v-data-table:only-child)),
.app-content :deep(.v-card:has(> .v-row .v-toolbar) .v-card-text:has(> .v-data-table:only-child)),
.app-content :deep(.v-card:has(> .v-col) .v-card-text:has(> .v-data-table:only-child)) {
  background: var(--sw-surface) !important;
  border-radius: var(--panel-radius) !important;
  border: 1px solid var(--sw-border) !important;
  box-shadow: var(--sw-shadow-sm) !important;
  padding: 0 !important;
  overflow: hidden;
}

@media (max-width: 960px) {
  .landing-shell {
    --shell-pad: 12px;
    --shell-gap: 12px;
    --panel-radius: 20px;
  }
}

/* ── Tablet: docked icon rail + hamburger opens full overlay drawer ── */
.landing-shell[data-layout="tablet"] .sidebar-collapse-btn,
.landing-shell[data-layout="mobile"] .sidebar-collapse-btn {
  display: none;
}

.landing-shell[data-layout="tablet"] .sidebar-float--drawer {
  position: fixed;
  left: var(--shell-pad);
  top: var(--shell-pad);
  bottom: var(--shell-pad);
  z-index: 50;
  width: min(288px, calc(100vw - (var(--shell-pad) * 2)));
  flex: none;
  height: auto;
  transform: translate3d(0, 0, 0);
  opacity: 1;
  pointer-events: auto;
  transition:
    transform var(--sidebar-duration) var(--sidebar-ease),
    opacity calc(var(--sidebar-duration) * 0.8) var(--sidebar-ease),
    width var(--sidebar-duration) var(--sidebar-ease);
}

/* ── Mobile: hidden sidebar, overlay drawer via hamburger ── */
.landing-shell[data-layout="mobile"] .sidebar-float {
  position: fixed;
  left: var(--shell-pad);
  top: var(--shell-pad);
  bottom: var(--shell-pad);
  z-index: 50;
  flex: 0 0 0;
  width: 0;
  min-width: 0;
  overflow: visible;
  height: auto;
  transform: translate3d(calc(-100% - var(--shell-pad) - 8px), 0, 0);
  transition:
    transform var(--sidebar-duration) var(--sidebar-ease),
    opacity calc(var(--sidebar-duration) * 0.8) var(--sidebar-ease),
    width var(--sidebar-duration) var(--sidebar-ease);
  pointer-events: none;
  opacity: 0;
}

.landing-shell[data-layout="mobile"] .sidebar-float--open {
  width: min(288px, calc(100vw - (var(--shell-pad) * 2)));
  transform: translate3d(0, 0, 0);
  pointer-events: auto;
  opacity: 1;
}

.landing-shell[data-layout="tablet"] .menu-btn,
.landing-shell[data-layout="mobile"] .menu-btn {
  color: var(--sw-primary) !important;
  background: rgba(var(--sw-primary-rgb), 0.08);
  border-radius: 10px;
}

.landing-shell[data-layout="tablet"] .main-column,
.landing-shell[data-layout="mobile"] .main-column {
  width: 100%;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
}

.landing-shell[data-layout="mobile"] .app-bar-float {
  min-height: 52px;
  padding: 8px 10px;
  gap: 8px;
}

.landing-shell[data-layout="mobile"] .app-bar-float__start {
  flex: 1 1 auto;
  min-width: 0;
  gap: 8px;
}

.landing-shell[data-layout="mobile"] .app-bar-float__end {
  flex: 0 0 auto;
  min-width: 40px;
}

.landing-shell[data-layout="mobile"] .org-selector {
  min-width: 0;
  flex: 1 1 auto;
  gap: 8px;
  padding-right: 0;
}

.landing-shell[data-layout="mobile"] .org-selector-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
}

.landing-shell[data-layout="mobile"] .org-selector-img {
  border-radius: 10px;
}

.landing-shell[data-layout="mobile"] .org-selector-text {
  min-width: 0;
  max-width: none;
}

.landing-shell[data-layout="mobile"] .org-selector-name {
  max-width: 34vw !important;
  font-size: 0.78rem;
}

.landing-shell[data-layout="mobile"] .org-selector-email {
  max-width: 34vw !important;
  font-size: 0.62rem;
}

@media (max-width: 1279px) {
  .org-selector-name,
  .org-selector-email {
    max-width: min(28vw, 220px);
  }
}
</style>
